import React, { useCallback, useMemo, useState } from 'react'
import type { Draft } from 'immer'
import { Building2, Search, SkipForward } from 'lucide-react'
import isError from 'lodash/isError'
import {
  ALLIANZ_GOVERNMENT_ID_TYPE_OPTIONS,
  allianzCompanyFormOverwrites,
  applyAllianzCompanyToForm,
  formatAllianzOverwriteDescription,
  lookupIdentifierPrefillFromOrgForm,
  type AllianzOrgFormFieldPath,
  type AllianzOrgFormValues
} from '@klear/organization-ui/apply-allianz-company'
import { useAllianzCompanySearch } from '@klear/organization-ui/use-allianz-company-search'
import { COUNTRY_CODE_OPTIONS } from '@klear/constants/countries'
import {
  ALLIANZ_COMPANY_STATUS_CODES,
  ALLIANZ_DEFAULT_PAGE_SIZE,
  ALLIANZ_ID_TYPE_DUNS,
  type AllianzGovernmentIdTypeCode
} from '@klear/allianz/constants'
import type { AllianzCompanySummary } from '@klear/allianz/schema'
import { useConfirmDialog } from '@klear/ui/components/alert-dialog'
import { badgeVariants } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Combobox } from '@klear/ui/components/combobox'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { SegmentedControl } from '@klear/ui/components/segmented-control'
import { cn } from '@klear/ui/lib/utils'

export type CompanyLookupFormUpdate<T extends AllianzOrgFormValues> = (
  recipe: (draft: Draft<T>) => void | AllianzOrgFormFieldPath | readonly AllianzOrgFormFieldPath[] | undefined
) => void

export type CompanyLookupProps<T extends AllianzOrgFormValues = AllianzOrgFormValues> = {
  values: T
  onFormUpdate: CompanyLookupFormUpdate<T>
  onApplied: () => void
  onSkip?: () => void
  required?: boolean
  includeAllianzEhid?: boolean
  overwriteEntityLabel?: string
  confirmOverwrites?: boolean

  showRatingStatus?: boolean
}

type LookupMode = 'id' | 'name'

const SEARCH_LIMIT = ALLIANZ_DEFAULT_PAGE_SIZE

const MODE_OPTIONS: { value: LookupMode; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'id', label: 'Government ID' }
]

function companyKey(company: AllianzCompanySummary, index: number): string {
  if (company.companyId) return company.companyId
  if (company.dunsNumber) return `duns:${company.dunsNumber}`
  return `row:${index}`
}

const STATUS_LABELS: Record<string, string> = { P: 'Provisional', C: 'Closed' }

function companyLocation(company: AllianzCompanySummary): string {
  return [company.city, company.state, company.countryCode].filter(Boolean).join(', ')
}

export function CompanyLookup<T extends AllianzOrgFormValues>({
  values,
  onFormUpdate,
  onSkip,
  onApplied,
  required = false,
  includeAllianzEhid = true,
  overwriteEntityLabel = 'organization',
  confirmOverwrites = false,
  showRatingStatus = false
}: CompanyLookupProps<T>) {
  const identifierPrefill = lookupIdentifierPrefillFromOrgForm(values)
  const [mode, setMode] = useState<LookupMode>(identifierPrefill.idValue ? 'id' : 'name')
  const [companyName, setCompanyName] = useState(values.name)
  const [countryCode, setCountryCode] = useState(values.address.country)
  const [idTypeCode, setIdTypeCode] = useState<AllianzGovernmentIdTypeCode | ''>(
    identifierPrefill.idTypeCode || ALLIANZ_ID_TYPE_DUNS
  )
  const [idValue, setIdValue] = useState(identifierPrefill.idValue)
  const [results, setResults] = useState<AllianzCompanySummary[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [searched, setSearched] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)
  const { confirm, ConfirmDialog } = useConfirmDialog()

  const { searchCompanies, isPending } = useAllianzCompanySearch()

  const selected = useMemo(() => results.find((c, i) => companyKey(c, i) === selectedId) ?? null, [results, selectedId])

  const clearResults = useCallback(() => {
    setResults([])
    setSelectedId(null)
    setSearched(false)
    setIncludeInactive(false)
    setLocalError(null)
  }, [])

  const applySelected = useCallback(
    (company: AllianzCompanySummary) => {
      onFormUpdate((draft) => applyAllianzCompanyToForm(draft, company, { includeAllianzEhid }))
      onApplied()
    },
    [includeAllianzEhid, onApplied, onFormUpdate]
  )

  const handleUseSelected = useCallback((): boolean => {
    if (!selected) {
      setLocalError('Select a company from the results')
      return false
    }
    setLocalError(null)
    const overwrites = confirmOverwrites
      ? allianzCompanyFormOverwrites(values, selected, { includeAllianzEhid }).filter(
          (item) => showRatingStatus || item.path !== 'allianzEhid'
        )
      : []
    if (overwrites.length === 0) {
      applySelected(selected)
      return true
    }
    confirm({
      title: `Overwrite existing ${overwriteEntityLabel} fields?`,
      description: formatAllianzOverwriteDescription(overwrites),
      confirmLabel: 'Overwrite',
      onConfirm: () => applySelected(selected)
    })
    return true
  }, [applySelected, confirm, confirmOverwrites, includeAllianzEhid, overwriteEntityLabel, selected, values])

  const runSearch = useCallback(
    async (options: { withInactive: boolean }) => {
      const name = companyName.trim()
      const trimmedId = idValue.trim()
      const identifier = idTypeCode && trimmedId ? { idTypeCode, idValue: trimmedId } : undefined

      if (mode === 'id' && !identifier) {
        setLocalError(idTypeCode ? `Enter the ${idTypeCode} to look up` : 'Choose an ID type and enter the ID')
        return
      }
      if (mode === 'name' && !name) {
        setLocalError('Enter a company name to search')
        return
      }

      setLocalError(null)
      setSearched(true)
      setSelectedId(null)
      setIncludeInactive(options.withInactive)
      try {
        const response = await searchCompanies({
          countryCode,
          limit: SEARCH_LIMIT,
          identifier: mode === 'id' ? identifier : undefined,
          companyName: mode === 'name' ? name : undefined,
          statuses: options.withInactive ? [...ALLIANZ_COMPANY_STATUS_CODES] : undefined
        })
        setResults(response.companies)
        if (response.companies.length === 1) {
          setSelectedId(companyKey(response.companies[0]!, 0))
        }
        onFormUpdate((draft) => {
          const dirty: AllianzOrgFormFieldPath[] = []
          if (!draft.address.country) {
            draft.address.country = countryCode
            dirty.push('address.country')
          }
          if (mode === 'name' && name && !draft.name.trim()) {
            draft.name = name
            dirty.push('name')
          }
          return dirty
        })
      } catch (error) {
        setResults([])
        setLocalError(isError(error) ? error.message : 'Company lookup failed')
      }
    },
    [companyName, countryCode, idTypeCode, idValue, mode, onFormUpdate, searchCompanies]
  )

  const canSkip = !required && onSkip != null
  const selectedHasNoEhid = showRatingStatus && selected != null && !selected.companyId

  const searchOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    if (isPending) return
    void runSearch({ withInactive: includeInactive })
  }

  const modeHint =
    mode === 'id'
      ? 'An ID matches one company exactly. Use this when the name on the paperwork may not be the registered name.'
      : 'Names return the closest matches, ranked. Add the full legal name for fewer, better matches.'

  return (
    <>
      {ConfirmDialog}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Company lookup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {required ? 'Find the company and select it to continue.' : 'Find the company, or skip this step.'}
          </p>

          <SegmentedControl
            options={MODE_OPTIONS}
            value={mode}
            onChange={(next) => {
              setMode(next)
              clearResults()
            }}
            className="w-full"
            disabled={isPending}
          />

          {mode === 'id' ? (
            <div className="space-y-2">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>ID type</Label>
                  <Combobox
                    value={idTypeCode}
                    options={ALLIANZ_GOVERNMENT_ID_TYPE_OPTIONS}
                    placeholder="Search ID types..."
                    triggerLabel="Select ID type"
                    onChange={(value) => {
                      setIdTypeCode((value || '') as AllianzGovernmentIdTypeCode | '')
                      clearResults()
                    }}
                    disabled={isPending}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-lookup-id-value">ID number</Label>
                  <Input
                    id="company-lookup-id-value"
                    value={idValue}
                    onChange={(e) => setIdValue(e.target.value)}
                    onKeyDown={searchOnEnter}
                    placeholder={idTypeCode ? `Enter ${idTypeCode}` : 'Choose an ID type first'}
                    disabled={isPending || !idTypeCode}
                    aria-describedby="company-lookup-mode-hint"
                  />
                </div>
              </div>
              <p id="company-lookup-mode-hint" className="text-xs text-muted-foreground">
                {modeHint}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="company-lookup-name">Company name</Label>
              <Input
                id="company-lookup-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                onKeyDown={searchOnEnter}
                placeholder="e.g. Apple Inc"
                disabled={isPending}
                aria-describedby="company-lookup-mode-hint"
              />
              <p id="company-lookup-mode-hint" className="text-xs text-muted-foreground">
                {modeHint}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Country</Label>
            <Combobox
              value={countryCode}
              options={COUNTRY_CODE_OPTIONS}
              placeholder="Search countries..."
              triggerLabel="Select country"
              onChange={(value) => {
                if (!value) return
                setCountryCode(value)
                clearResults()
              }}
              disabled={isPending}
            />
          </div>

          {localError ? (
            <p className="text-sm text-destructive" role="alert">
              {localError}
            </p>
          ) : null}

          <div className="flex flex-wrap justify-between gap-2">
            {canSkip ? (
              <Button type="button" variant="outline" onClick={onSkip} disabled={isPending}>
                <SkipForward className="mr-1 h-4 w-4" />
                Skip
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => void runSearch({ withInactive: includeInactive })}
                disabled={isPending}
              >
                <Search className="mr-1 h-4 w-4" />
                {isPending ? 'Searching…' : mode === 'id' ? 'Look up' : 'Search'}
              </Button>
              {results.length > 0 ? (
                <Button type="button" onClick={handleUseSelected} disabled={!selected || isPending}>
                  Use selected
                </Button>
              ) : null}
            </div>
          </div>

          {searched && !isPending ? (
            <div className="space-y-2">
              <Label>Results</Label>

              {results.length === 0 ? (
                <div className="space-y-2 rounded-md border border-dashed px-3 py-4">
                  <p className="text-sm text-muted-foreground">
                    {mode === 'id'
                      ? `No company in ${countryCode} has that ${idTypeCode || 'ID'}. Check the number, or search by name.`
                      : `No company in ${countryCode} matches that name. Try the registered legal name, or look up a government ID.`}
                  </p>
                  {!includeInactive ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void runSearch({ withInactive: true })}
                      disabled={isPending}
                    >
                      Include closed and dormant companies
                    </Button>
                  ) : null}
                </div>
              ) : (
                <ul className="divide-y rounded-md border">
                  {results.map((company, index) => {
                    const key = companyKey(company, index)
                    const active = key === selectedId
                    const statusLabel = company.companyStatusCode ? STATUS_LABELS[company.companyStatusCode] : undefined
                    const location = companyLocation(company)
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          aria-pressed={active}
                          className={cn(
                            'flex w-full flex-col items-start gap-1 px-3 py-2.5 text-left text-sm',
                            'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                            active && 'bg-muted'
                          )}
                          onClick={() => setSelectedId(key)}
                        >
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{company.companyName ?? company.dunsNumber}</span>
                            {statusLabel ? (
                              <span className={cn(badgeVariants({ variant: 'outline' }), 'font-normal')}>
                                {statusLabel}
                              </span>
                            ) : null}
                            {showRatingStatus && !company.companyId ? (
                              <span className={cn(badgeVariants({ variant: 'secondary' }), 'font-normal')}>
                                Not Yet Rated
                              </span>
                            ) : null}
                          </span>
                          <span className="text-muted-foreground">
                            {location}
                            {company.dunsNumber ? `${location ? ' · ' : ''}DUNS ${company.dunsNumber}` : ''}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}

              {selectedHasNoEhid ? (
                <p className="text-xs text-muted-foreground">
                  This company has no credit rating or limit yet. Selecting it fills in the name and address only.
                </p>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </>
  )
}
